import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { PermissionGate } from "../../auth/access";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/EditOutlined";
import { nanoid } from "nanoid";
import type {
  SchemeContent,
  SchemeStation,
  LocalizedString,
  SchemeManifest,
  SchemeSlotDef,
  SlotType,
  SlotSize,
  SlotFill,
  BackgroundMode,
  InstagramDisplay,
  InstagramConnectionStatus,
} from "@shared/types/foyer";
import { usePremiseScope } from "../../context/PremiseScopeContext";
import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import AssetField from "../AssetField";
import LocalizedTextField from "../LocalizedTextField";
import {
  foyerApi,
  type AppRecord,
  type RadioStationRecord,
} from "../../http/foyer";
import type { RoomOffer } from "@shared/types/roomOffer";
import { resolveLocalized } from "@shared/i18n";
import { orderCatalogue } from "@shared/catalogue-order";
import { CARE_ICON_OPTIONS } from "@shared/schemes/careIcons";
import { fieldGrid, EDITOR_MAX_WIDTH } from "./editorLayout";
import LocalizedSectionsEditor from "./LocalizedSectionsEditor";
import CatalogueGrid from "./CatalogueGrid";
import {
  certificationOf,
  certificationReason,
} from "@shared/app-certification";
import EditorCard from "./EditorCard";
import { MAX_SLOT_FILLS, slotFills } from "@shared/schemeSlots";
import { tokens } from "../../theme";
import { ItemFormSurface } from "../EditDrawer";
import type { Translate } from "../../i18n/translate";
import { useFormat, type Formatters } from "../../i18n/format";
import { errorText } from "../../utils/apiError";

const URL_PLACEHOLDER = "https://…";
const SEPARATOR = " · ";
const QR_URL_PLACEHOLDER = "https://hotel.example/menu";

/**
 * The structured content a scheme renders, split into standalone panes
 * (Branding / Apps / Radio / Information) so each can be its own sub-section in
 * the editor. Every pane takes the full `SchemeContent` and emits the full
 * updated content, editing only its slice. The content shape is shared by all
 * templates - only the design tokens vary per-template.
 */
export interface ContentPaneProps {
  content: SchemeContent;
  supportedLocales: string[];
  defaultLocale: string;
  onChange: (next: SchemeContent) => void;
}

// ── Branding ─────────────────────────────────────────────────────────────

export function BrandingEditor({ content, onChange }: ContentPaneProps) {
  const { t } = useTranslation(["templates", "common"]);
  const setBranding = (part: Partial<SchemeContent["branding"]>) =>
    onChange({ ...content, branding: { ...content.branding, ...part } });
  const b = content.branding;
  return (
    <Stack spacing={2}>
      <Typography variant="h6">{t("contentEditor.branding.title")}</Typography>
      <Box sx={fieldGrid(280)}>
        <TextField
          size="small"
          fullWidth
          label={t("contentEditor.branding.hotelName")}
          value={b.hotelName}
          onChange={(e) => setBranding({ hotelName: e.target.value })}
        />
        <TextField
          size="small"
          fullWidth
          label={t("contentEditor.branding.city")}
          value={b.city ?? ""}
          onChange={(e) => setBranding({ city: e.target.value })}
        />
        <AssetField
          label={t("contentEditor.branding.logo")}
          value={b.logoUrl ?? ""}
          preview
          onChange={(url) => setBranding({ logoUrl: url })}
        />
      </Box>
    </Stack>
  );
}

// ── Background & photos ──────────────────────────────────────────────────

/**
 * Choose the home background (solid colour / photo / photo + tint) and pick the
 * template's photo(s) from the media library. Only templates that declare image
 * slots (the photo-forward launchers) expose anything here.
 */
export function BackgroundEditor({
  content,
  manifest,
  onChange,
}: ContentPaneProps & { manifest: SchemeManifest }) {
  const { t } = useTranslation(["templates", "common"]);
  const slots = manifest.imageSlots ?? [];
  const mode: BackgroundMode = content.background?.mode ?? "photoTint";
  const setMode = (m: BackgroundMode) =>
    onChange({ ...content, background: { ...content.background, mode: m } });
  const setImage = (id: string, url: string) =>
    onChange({
      ...content,
      images: { ...(content.images ?? {}), [id]: url },
    });

  if (slots.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("contentEditor.background.none")}
      </Typography>
    );
  }
  return (
    <Stack spacing={2}>
      <Typography variant="h6">
        {t("contentEditor.background.title")}
      </Typography>
      <TextField
        size="small"
        select
        label={t("contentEditor.background.homeBackground")}
        value={mode}
        onChange={(e) => setMode(e.target.value as BackgroundMode)}
        sx={{ maxWidth: 280 }}
        helperText={t("contentEditor.background.homeBackgroundHelp")}
      >
        <MenuItem value="color">
          {t("contentEditor.background.modes.color")}
        </MenuItem>
        <MenuItem value="photo">
          {t("contentEditor.background.modes.photo")}
        </MenuItem>
        <MenuItem value="photoTint">
          {t("contentEditor.background.modes.photoTint")}
        </MenuItem>
      </TextField>
      <Box sx={fieldGrid(300)}>
        {slots.map((slot) => (
          <AssetField
            key={slot.id}
            label={
              slot.aspect
                ? t("contentEditor.background.slotWithAspect", {
                    label: slot.label,
                    aspect: slot.aspect,
                  })
                : slot.label
            }
            value={content.images?.[slot.id] ?? ""}
            preview
            onChange={(url) => setImage(slot.id, url)}
          />
        ))}
      </Box>
    </Stack>
  );
}

// ── Apps ─────────────────────────────────────────────────────────────────

/**
 * Apps are the global catalogue, toggled on/off per premise - same model as the
 * global radio channels. There are no custom apps: the catalogue is edited
 * centrally (Apps page); here you only choose which ones show on this premise,
 * and in what order (`appOrder` - drag a tile).
 */
export function AppsEditor({
  content,
  global,
  isCare = false,
  onChange,
}: ContentPaneProps & {
  /** The global catalogue, fetched once by `TemplateEditor` and shared with the
   *  Screens board so the two cannot disagree about what is pinned. `null`
   *  while it is still loading. */
  global: AppRecord[] | null;
  /** A care premise is never offered a certified app (NFLX-6). */
  isCare?: boolean;
}) {
  const { t } = useTranslation(["templates", "common"]);
  const disabled = content.disabledAppIds ?? [];
  const isOn = (id: string) => !disabled.includes(id);
  const toggle = (id: string) =>
    onChange({
      ...content,
      disabledAppIds: isOn(id)
        ? [...disabled, id]
        : disabled.filter((x) => x !== id),
    });

  // A certified app that is ON is pinned first and cannot be dragged (NFLX-4).
  // It is still the operator's to switch OFF - the agreement asks that Netflix
  // come first where it is carried, not that a hotel carry it - so this is a
  // pin, never a lock, and the switch below stays live.
  const pinnedBy = useMemo(() => {
    const m = new Map<string, string>();
    if (isCare) return m;
    for (const a of global ?? []) {
      const cert = certificationOf(a);
      if (cert && !disabled.includes(a.id))
        m.set(a.id, certificationReason(cert));
    }
    return m;
    // `disabled` is derived from `content` each render, so key the memo on the
    // stored array rather than on a fresh one.
  }, [global, content.disabledAppIds, isCare]);

  // A certified app is not offered at all on a care premise (NFLX-6): the
  // agreement it ships under is built on check-in/check-out, and a ward has no
  // check-in clock. Removed from the grid rather than shown switched off - an
  // off switch invites someone to turn it on, and the server would refuse.
  const offerable = useMemo(
    () =>
      isCare
        ? (global ?? []).filter((a) => certificationOf(a) === null)
        : (global ?? []),
    [global, isCare]
  );

  // The grid shows the catalogue in the order the guest gets it - off entries
  // included, so a tile returns to its place when it's switched back on. A
  // pinned entry is hoisted to the head exactly as `withGlobalApps` does, so
  // the board shows the arrangement the guest will actually get rather than the
  // one the stored order asks for.
  const ordered = useMemo(() => {
    const list = orderCatalogue(offerable, content.appOrder);
    if (!pinnedBy.size) return list;
    return [
      ...list.filter((a) => pinnedBy.has(a.id)),
      ...list.filter((a) => !pinnedBy.has(a.id)),
    ];
  }, [offerable, content.appOrder, pinnedBy]);
  const hasOrder = (content.appOrder?.length ?? 0) > 0;
  const onCount = offerable.filter((g) => isOn(g.id)).length ?? 0;

  return (
    <Stack spacing={2} sx={{ maxWidth: EDITOR_MAX_WIDTH }}>
      <Typography variant="h6">{t("contentEditor.apps.title")}</Typography>

      <EditorCard
        eyebrow={t("contentEditor.apps.eyebrow")}
        hint={
          pinnedBy.size
            ? t("contentEditor.apps.hintPinned")
            : t("contentEditor.apps.hint")
        }
        action={
          <Stack direction="row" alignItems="center" spacing={2}>
            {global && (
              <Typography variant="meta" sx={{ color: tokens.inkFaint }}>
                {t("contentEditor.catalogue.onCount", {
                  on: onCount,
                  total: global.length,
                })}
              </Typography>
            )}
            {hasOrder && (
              <Link
                component="button"
                type="button"
                variant="body2"
                underline="hover"
                onClick={() => onChange({ ...content, appOrder: [] })}
              >
                {t("contentEditor.catalogue.sortAz")}
              </Link>
            )}
            <Link
              component={RouterLink}
              to="/apps"
              variant="body2"
              underline="hover"
            >
              {t("contentEditor.catalogue.manage")}
            </Link>
          </Stack>
        }
      >
        {global === null ? (
          <Typography variant="body2" color="text.secondary">
            {t("common:states.loading")}
          </Typography>
        ) : global.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("contentEditor.apps.empty")}
          </Typography>
        ) : (
          <CatalogueGrid
            data-testid="apps-grid"
            items={ordered}
            noun="app"
            isOn={isOn}
            onToggle={toggle}
            pinnedBy={pinnedBy}
            onReorder={(appOrder) => onChange({ ...content, appOrder })}
          />
        )}
      </EditorCard>
    </Stack>
  );
}

// ── Radio stations ─────────────────────────────────────────────────────────

export function StationsEditor({
  content,
  supportedLocales,
  defaultLocale,
  onChange,
}: ContentPaneProps) {
  const { t } = useTranslation(["templates", "common"]);
  const loc = { supportedLocales, defaultLocale };
  // Per-premise CUSTOM channels. Add is a modal and edit a right-hand drawer -
  // no inline fields in the pane (`dialogIdx`: "new" = adding, number = editing
  // a row).
  const stations = content.stations;
  const setStations = (s: SchemeStation[]) =>
    onChange({ ...content, stations: s });
  const [dialogIdx, setDialogIdx] = useState<number | "new" | null>(null);
  const saveStation = (st: SchemeStation) => {
    setStations(
      dialogIdx === "new"
        ? [...stations, st]
        : stations.map((s, idx) => (idx === dialogIdx ? st : s))
    );
    setDialogIdx(null);
  };

  // Per-premise on/off selection over the global catalogue. Global channels are
  // edited centrally (catalogue page) - here you only toggle which ones show.
  const disabled = content.disabledStationIds ?? [];
  const isOn = (id: string) => !disabled.includes(id);
  const toggle = (id: string) =>
    onChange({
      ...content,
      disabledStationIds: isOn(id)
        ? [...disabled, id]
        : disabled.filter((x) => x !== id),
    });

  const [global, setGlobal] = useState<RadioStationRecord[] | null>(null);
  useEffect(() => {
    let alive = true;
    foyerApi
      .listRadioStations()
      .then((list) => alive && setGlobal(Array.isArray(list) ? list : []))
      .catch(() => alive && setGlobal([]));
    return () => {
      alive = false;
    };
  }, []);

  // Same contract as Apps. Custom channels are NOT in `stationOrder` - they
  // follow the global list in their own array order (see `withGlobalStations`).
  const ordered = useMemo(
    () => orderCatalogue(global ?? [], content.stationOrder),
    [global, content.stationOrder]
  );
  const hasOrder = (content.stationOrder?.length ?? 0) > 0;
  const onCount = global?.filter((g) => isOn(g.id)).length ?? 0;

  return (
    <Stack spacing={2} sx={{ maxWidth: EDITOR_MAX_WIDTH }}>
      <Typography variant="h6">{t("contentEditor.radio.title")}</Typography>

      {/* Global catalogue - toggle on/off per premise; not editable here. */}
      <EditorCard
        eyebrow={t("contentEditor.radio.eyebrow")}
        hint={t("contentEditor.radio.hint")}
        action={
          <Stack direction="row" alignItems="center" spacing={2}>
            {global && (
              <Typography variant="meta" sx={{ color: tokens.inkFaint }}>
                {t("contentEditor.catalogue.onCount", {
                  on: onCount,
                  total: global.length,
                })}
              </Typography>
            )}
            {hasOrder && (
              <Link
                component="button"
                type="button"
                variant="body2"
                underline="hover"
                onClick={() => onChange({ ...content, stationOrder: [] })}
              >
                {t("contentEditor.catalogue.sortAz")}
              </Link>
            )}
            <Link
              component={RouterLink}
              to="/radio-stations"
              variant="body2"
              underline="hover"
            >
              {t("contentEditor.catalogue.manage")}
            </Link>
          </Stack>
        }
      >
        {global === null ? (
          <Typography variant="body2" color="text.secondary">
            {t("common:states.loading")}
          </Typography>
        ) : global.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("contentEditor.radio.empty")}
          </Typography>
        ) : (
          <CatalogueGrid
            data-testid="stations-grid"
            items={ordered}
            noun="channel"
            isOn={isOn}
            onToggle={toggle}
            onReorder={(stationOrder) => onChange({ ...content, stationOrder })}
          />
        )}
      </EditorCard>

      {/* Per-premise custom channels - compact rows; edit/add via dialog. */}
      <EditorCard
        eyebrow={t("contentEditor.radio.customEyebrow", {
          total: stations.length,
        })}
      >
        {stations.length > 0 && (
          <Stack
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            {stations.map((s, i) => (
              <Stack
                key={s.id}
                direction="row"
                alignItems="center"
                sx={{
                  pl: 1.5,
                  pr: 0.5,
                  py: 0.5,
                  borderTop: i === 0 ? 0 : "1px solid",
                  borderColor: "divider",
                }}
              >
                <Stack
                  onClick={() => setDialogIdx(i)}
                  sx={{ flex: 1, cursor: "pointer" }}
                >
                  <Typography variant="body2">{s.name}</Typography>
                  {(() => {
                    const tag = resolveLocalized(
                      s.tag,
                      defaultLocale,
                      defaultLocale
                    );
                    return tag ? (
                      <Typography variant="caption" color="text.secondary">
                        {tag}
                      </Typography>
                    ) : null;
                  })()}
                </Stack>
                <Tooltip title={t("common:buttons.edit")}>
                  <IconButton size="small" onClick={() => setDialogIdx(i)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t("common:buttons.delete")}>
                  <IconButton
                    size="small"
                    aria-label={t("contentEditor.radio.deleteStation")}
                    onClick={() =>
                      setStations(stations.filter((_, idx) => idx !== i))
                    }
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            ))}
          </Stack>
        )}
        <Button
          startIcon={<AddIcon />}
          onClick={() => setDialogIdx("new")}
          sx={{ alignSelf: "flex-start", mt: stations.length > 0 ? 1.5 : 0 }}
        >
          {t("contentEditor.radio.addCustom")}
        </Button>
      </EditorCard>

      {dialogIdx !== null && (
        <CustomStationDialog
          initial={dialogIdx === "new" ? null : stations[dialogIdx]}
          {...loc}
          onClose={() => setDialogIdx(null)}
          onSave={saveStation}
        />
      )}
    </Stack>
  );
}

/** Add (modal) / edit (right drawer) one per-premise custom channel - same
 *  fields as the global catalogue, so a custom channel behaves like a global
 *  one. `onSave` runs after the surface has closed. */
export function CustomStationDialog({
  initial,
  supportedLocales,
  defaultLocale,
  onClose,
  onSave,
}: {
  initial: SchemeStation | null;
  supportedLocales: string[];
  defaultLocale: string;
  onClose: () => void;
  onSave: (s: SchemeStation) => void;
}) {
  const { t } = useTranslation(["templates", "common"]);
  const loc = { supportedLocales, defaultLocale };
  const [name, setName] = useState(initial?.name ?? "");
  const [tag, setTag] = useState<LocalizedString>(initial?.tag ?? "");
  const [streamUrl, setStreamUrl] = useState(initial?.streamUrl ?? "");
  const [metadataId, setMetadataId] = useState(initial?.metadataId ?? "");
  const [logoUrl, setLogoUrl] = useState(initial?.logoUrl ?? "");

  const save = () =>
    onSave({
      id: initial?.id ?? nanoid(6),
      name: name.trim() || "Untitled channel",
      tag,
      freq: initial?.freq ?? "",
      streamUrl: streamUrl.trim() || undefined,
      metadataId: metadataId.trim() || undefined,
      logoUrl: logoUrl.trim() || undefined,
    });

  // Adding is a modal, editing an existing channel slides in from the right.
  // Either way the result goes to the caller only once the surface has
  // finished closing, so the caller can unmount it without cutting the slide.
  return (
    <ItemFormSurface
      mode={initial ? "edit" : "create"}
      onClose={onClose}
      title={
        initial
          ? t("contentEditor.customStation.editTitle")
          : t("contentEditor.customStation.newTitle")
      }
      maxWidth="xs"
      actions={(dismiss) => (
        <>
          <Button onClick={() => dismiss()}>
            {t("common:buttons.cancel")}
          </Button>
          <Button variant="contained" onClick={() => dismiss(save)}>
            {initial ? t("common:buttons.save") : t("common:buttons.add")}
          </Button>
        </>
      )}
    >
      <Stack spacing={1.5} sx={{ mt: 0.5 }}>
        <TextField
          size="small"
          label={t("common:labels.name")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
        <LocalizedTextField
          label={t("contentEditor.customStation.tag")}
          value={tag}
          {...loc}
          onChange={(v: LocalizedString) => setTag(v)}
        />
        <TextField
          size="small"
          label={t("contentEditor.customStation.streamUrl")}
          placeholder={URL_PLACEHOLDER}
          value={streamUrl}
          onChange={(e) => setStreamUrl(e.target.value)}
        />
        <TextField
          size="small"
          label={t("contentEditor.customStation.metadataId")}
          value={metadataId}
          onChange={(e) => setMetadataId(e.target.value)}
        />
        <AssetField
          label={t("contentEditor.customStation.logo")}
          value={logoUrl}
          preview
          onChange={(url: string) => setLogoUrl(url)}
        />
      </Stack>
    </ItemFormSurface>
  );
}

// ── Information directory ───────────────────────────────────────────────────

export function InfoEditor({
  content,
  supportedLocales,
  defaultLocale,
  onChange,
}: ContentPaneProps) {
  const { t } = useTranslation(["templates", "common"]);
  const sections = content.infoSections;
  return (
    <Stack spacing={2} sx={{ maxWidth: 720 }}>
      <Typography variant="h6">
        {t("contentEditor.info.title", { total: sections.length })}
      </Typography>
      <LocalizedSectionsEditor
        sections={sections}
        supportedLocales={supportedLocales}
        defaultLocale={defaultLocale}
        showEmbedUrl
        onChange={(s) => onChange({ ...content, infoSections: s })}
      />
    </Stack>
  );
}

// ── Wi-Fi ────────────────────────────────────────────────────────────────

export function WifiEditor({ content, onChange }: ContentPaneProps) {
  const { t } = useTranslation(["templates", "common"]);
  const wifi = content.wifi ?? { ssid: "" };
  const setWifi = (part: Partial<SchemeContent["wifi"]>) =>
    onChange({ ...content, wifi: { ...wifi, ...part } });
  return (
    <Stack spacing={2} sx={{ maxWidth: 480 }}>
      <Typography variant="h6">{t("contentEditor.wifi.title")}</Typography>
      <TextField
        size="small"
        label={t("contentEditor.wifi.ssid")}
        value={wifi.ssid}
        onChange={(e) => setWifi({ ssid: e.target.value })}
      />
      <TextField
        size="small"
        label={t("contentEditor.wifi.password")}
        value={wifi.password ?? ""}
        onChange={(e) => setWifi({ password: e.target.value || undefined })}
      />
      <FormControlLabel
        control={
          <Switch
            checked={!!wifi.showQr}
            onChange={(e) => setWifi({ showQr: e.target.checked })}
          />
        }
        label={t("contentEditor.wifi.showQr")}
      />
    </Stack>
  );
}

// ── Welcome / Messages ─────────────────────────────────────────────────────

export function WelcomeEditor({
  content,
  supportedLocales,
  defaultLocale,
  onChange,
}: ContentPaneProps) {
  const loc = { supportedLocales, defaultLocale };
  const { t } = useTranslation(["templates", "common"]);
  const w = content.welcome ?? { title: "", body: "" };
  const setW = (part: Partial<SchemeContent["welcome"]>) =>
    onChange({ ...content, welcome: { ...w, ...part } });
  return (
    <Stack spacing={2} sx={{ maxWidth: 640 }}>
      <Typography variant="h6">{t("contentEditor.welcome.title")}</Typography>
      <LocalizedTextField
        label={t("common:labels.title")}
        value={w.title}
        {...loc}
        onChange={(v: LocalizedString) => setW({ title: v })}
      />
      <LocalizedTextField
        label={t("contentEditor.welcome.message")}
        value={w.body}
        multiline
        rows={6}
        {...loc}
        onChange={(v: LocalizedString) => setW({ body: v })}
      />
    </Stack>
  );
}

// ── Relatives (Anhöriga) ───────────────────────────────────────────────────

export function RelativesEditor({
  content,
  supportedLocales,
  defaultLocale,
  onChange,
}: ContentPaneProps) {
  const { t } = useTranslation(["templates", "common"]);
  const rel = content.relatives ?? { sections: [] };
  const setRel = (part: Partial<SchemeContent["relatives"]>) =>
    onChange({ ...content, relatives: { ...rel, ...part } });
  return (
    <Stack spacing={2} sx={{ maxWidth: 720 }}>
      <Typography variant="h6">{t("contentEditor.relatives.title")}</Typography>
      <TextField
        size="small"
        label={t("contentEditor.relatives.portalUrl")}
        placeholder={URL_PLACEHOLDER}
        value={rel.embedUrl ?? ""}
        onChange={(e) =>
          setRel({ embedUrl: e.target.value.trim() || undefined })
        }
        helperText={t("contentEditor.relatives.portalUrlHelp")}
        fullWidth
      />
      <Typography variant="subtitle2" color="text.secondary">
        {t("contentEditor.relatives.sections", {
          total: rel.sections.length,
        })}
      </Typography>
      <LocalizedSectionsEditor
        sections={rel.sections}
        supportedLocales={supportedLocales}
        defaultLocale={defaultLocale}
        onChange={(s) => setRel({ sections: s })}
      />
    </Stack>
  );
}

// ── Instagram ───────────────────────────────────────────────────────────────

export function InstagramEditor({
  content,
  supportedLocales,
  defaultLocale,
  onChange,
}: ContentPaneProps) {
  const loc = { supportedLocales, defaultLocale };
  const { t } = useTranslation(["templates", "common"]);
  const ig = content.instagram ?? { handle: "" };
  const setIg = (part: Partial<SchemeContent["instagram"]>) =>
    onChange({ ...content, instagram: { ...ig, ...part } });
  // Legacy back-compat: pre-`display` configs are pure "invite" screens.
  const display: InstagramDisplay = ig.display ?? "invite";
  const feedNeedsConnection = display === "feed" || display === "both";
  const feedCount = ig.feedCount ?? 6;
  return (
    <Stack spacing={2.5} sx={{ maxWidth: 520 }}>
      <Typography variant="h6">{t("contentEditor.instagram.title")}</Typography>
      <Typography variant="body2" color="text.secondary">
        {t("contentEditor.instagram.intro")}
      </Typography>

      <FormControl>
        <FormLabel
          sx={{
            typography: "bodySm",
            fontWeight: "fontWeightSemibold",
            mb: 0.5,
          }}
          id="ig-display-label"
        >
          {t("contentEditor.instagram.displayLabel")}
        </FormLabel>
        <RadioGroup
          aria-labelledby="ig-display-label"
          value={display}
          onChange={(e) =>
            setIg({ display: e.target.value as InstagramDisplay })
          }
        >
          <FormControlLabel
            value="feed"
            control={<Radio size="small" />}
            label={t("contentEditor.instagram.display.feed")}
          />
          <FormControlLabel
            value="invite"
            control={<Radio size="small" />}
            label={t("contentEditor.instagram.display.invite")}
          />
          <FormControlLabel
            value="both"
            control={<Radio size="small" />}
            label={t("contentEditor.instagram.display.both")}
          />
        </RadioGroup>
      </FormControl>

      <TextField
        size="small"
        label={t("contentEditor.instagram.handle")}
        placeholder="grandhotel"
        value={ig.handle}
        onChange={(e) =>
          setIg({ handle: e.target.value.replace(/^@/, "").trim() })
        }
        helperText={t("contentEditor.instagram.handleHelp")}
      />
      <LocalizedTextField
        label={t("contentEditor.instagram.caption")}
        value={ig.caption ?? ""}
        multiline
        rows={2}
        {...loc}
        onChange={(v: LocalizedString) => setIg({ caption: v || undefined })}
      />

      {feedNeedsConnection && (
        <TextField
          select
          size="small"
          label={t("contentEditor.instagram.feedCount")}
          value={feedCount}
          onChange={(e) => setIg({ feedCount: Number(e.target.value) })}
          sx={{ maxWidth: 220 }}
        >
          {[3, 4, 6, 8, 9, 12].map((n) => (
            <MenuItem key={n} value={n}>
              {t("contentEditor.instagram.postCount", { count: n })}
            </MenuItem>
          ))}
        </TextField>
      )}

      <Divider />
      <InstagramConnection
        handle={ig.handle}
        onHandle={(h) => setIg({ handle: h })}
        feedNeeded={feedNeedsConnection}
      />
    </Stack>
  );
}

/** Connect / disconnect the premise's Instagram account via OAuth. Opens the
 *  Meta consent flow in a popup and listens for the `foyer:instagram` message
 *  the callback page posts back. */
function InstagramConnection({
  handle,
  onHandle,
  feedNeeded,
}: {
  handle: string;
  onHandle: (handle: string) => void;
  feedNeeded: boolean;
}) {
  const { t } = useTranslation(["templates", "common"]);
  const fmt = useFormat();
  const premiseId = usePremiseScope();
  const [status, setStatus] = useState<InstagramConnectionStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    if (!premiseId) return;
    try {
      const s = await foyerApi.instagramStatus(premiseId);
      setStatus(s);
      // Keep the handle field in sync with the connected account.
      if (s.connected && s.username && s.username !== handle) {
        onHandle(s.username);
      }
    } catch {
      setStatus(null);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [premiseId]);

  // Listen for the popup's completion message.
  useEffect(() => {
    function onMsg(e: MessageEvent) {
      if (e.origin !== window.location.origin) return;
      const data = e.data as
        | { type?: string; payload?: { ok?: boolean; error?: string } }
        | undefined;
      if (data?.type !== "foyer:instagram") return;
      setLoading(false);
      if (data.payload?.ok) {
        setError(null);
        void refresh();
      } else {
        setError(friendlyError(t, data.payload?.error));
      }
    }
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [premiseId, handle]);

  const connect = () => {
    if (!premiseId) return;
    setError(null);
    setLoading(true);
    const w = 600;
    const h = 720;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;
    const popup = window.open(
      foyerApi.instagramConnectUrl(premiseId),
      "foyer-instagram",
      `popup,width=${w},height=${h},left=${left},top=${top}`
    );
    if (!popup) {
      setLoading(false);
      setError(t("contentEditor.instagramConnection.popupBlocked"));
    }
  };

  const disconnect = async () => {
    if (!premiseId) return;
    setLoading(true);
    setError(null);
    try {
      await foyerApi.disconnectInstagram(premiseId);
      await refresh();
    } catch (err) {
      setError(errorText(err, t));
    } finally {
      setLoading(false);
    }
  };

  if (!premiseId) {
    return (
      <Alert severity="info" variant="outlined">
        {t("contentEditor.instagramConnection.needsPremise")}
      </Alert>
    );
  }

  const connected = status?.connected === true;
  return (
    <Stack spacing={1.5}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
      >
        <Typography variant="subtitle2">
          {t("contentEditor.instagramConnection.connectedAccount")}
        </Typography>
        {connected ? (
          <Chip
            size="small"
            color="success"
            label={`@${status?.username ?? handle}`}
          />
        ) : (
          <Chip
            size="small"
            variant="outlined"
            label={t("contentEditor.instagramConnection.notConnected")}
          />
        )}
        {loading && <CircularProgress size={16} />}
      </Stack>

      {connected && status?.expiresAt && (
        <Typography variant="caption" color="text.secondary">
          {t("contentEditor.instagramConnection.tokenValid", {
            date: fmt.date(status.expiresAt),
          })}
        </Typography>
      )}

      {!connected && feedNeeded && (
        <Alert severity="warning" variant="outlined">
          {t("contentEditor.instagramConnection.feedNeedsAccount")}
        </Alert>
      )}

      {error && (
        <Alert
          severity="error"
          variant="outlined"
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <PermissionGate resource="tv-layout" level="edit">
        {" "}
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            size="small"
            onClick={connect}
            disabled={loading}
          >
            {connected
              ? t("contentEditor.instagramConnection.reconnect")
              : t("contentEditor.instagramConnection.connect")}
          </Button>
          {connected && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={disconnect}
              disabled={loading}
            >
              {t("contentEditor.instagramConnection.disconnect")}
            </Button>
          )}
        </Stack>
      </PermissionGate>
      <Typography variant="caption" color="text.secondary">
        {t("contentEditor.instagramConnection.requirements")}
      </Typography>
    </Stack>
  );
}

function friendlyError(t: Translate, code?: string): string {
  switch (code) {
    case "instagram_not_configured":
      return t(
        "templates:contentEditor.instagramConnection.errors.notConfigured"
      );
    case "invalid_state":
      return t(
        "templates:contentEditor.instagramConnection.errors.invalidState"
      );
    case "oauth_exchange_failed":
      return t(
        "templates:contentEditor.instagramConnection.errors.oauthFailed"
      );
    case "premise_not_found":
      return t(
        "templates:contentEditor.instagramConnection.errors.premiseNotFound"
      );
    default:
      return (
        code || t("templates:contentEditor.instagramConnection.errors.generic")
      );
  }
}

// ── Weather ──────────────────────────────────────────────────────────────────

export function WeatherEditor({ content, onChange }: ContentPaneProps) {
  const { t } = useTranslation(["templates", "common"]);
  const w = content.weather ?? {};
  const units = w.units ?? "c";
  return (
    <Stack spacing={2} sx={{ maxWidth: 480 }}>
      <Typography variant="h6">{t("contentEditor.weather.title")}</Typography>
      <Typography variant="body2" color="text.secondary">
        {t("contentEditor.weather.intro")}
      </Typography>
      <TextField
        select
        size="small"
        label={t("contentEditor.weather.units")}
        value={units}
        onChange={(e) =>
          onChange({
            ...content,
            weather: { ...w, units: e.target.value as "c" | "f" },
          })
        }
        sx={{ maxWidth: 220 }}
      >
        <MenuItem value="c">{t("contentEditor.weather.celsius")}</MenuItem>
        <MenuItem value="f">{t("contentEditor.weather.fahrenheit")}</MenuItem>
      </TextField>
    </Stack>
  );
}

// ── Home cards (Care) ─────────────────────────────────────────────────────

/** The Care home cards whose icon + text can be customised. */
const CARE_CARDS: { id: string; label: string }[] = [
  { id: "tv", label: "TV" },
  { id: "apps", label: "Nöje" },
  { id: "radio", label: "Radio" },
  { id: "info", label: "Info" },
  { id: "relatives", label: "Anhöriga" },
];

export function HomeCardsEditor({
  content,
  supportedLocales,
  defaultLocale,
  onChange,
}: ContentPaneProps) {
  const loc = { supportedLocales, defaultLocale };
  const { t } = useTranslation(["templates", "common"]);
  const icons = content.homeIcons ?? {};
  const cards = content.homeCards ?? {};
  const setIcon = (id: string, key: string) =>
    onChange({ ...content, homeIcons: { ...icons, [id]: key } });
  const setCard = (
    id: string,
    part: Partial<{ title: LocalizedString; description: LocalizedString }>
  ) =>
    onChange({
      ...content,
      homeCards: { ...cards, [id]: { ...cards[id], ...part } },
    });
  return (
    <Stack spacing={2.5}>
      <Typography variant="h6">{t("contentEditor.homeCards.title")}</Typography>
      <Typography variant="body2" color="text.secondary">
        {t("contentEditor.homeCards.intro")}
      </Typography>
      <Box sx={fieldGrid(320)}>
        {CARE_CARDS.map((card) => (
          <Box
            key={card.id}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              p: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              {card.label}
            </Typography>
            <Stack spacing={1.5}>
              <TextField
                select
                size="small"
                label={t("contentEditor.homeCards.icon")}
                value={icons[card.id] ?? card.id}
                onChange={(e) => setIcon(card.id, e.target.value)}
              >
                {CARE_ICON_OPTIONS.map((o) => (
                  <MenuItem key={o.key} value={o.key}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
              <LocalizedTextField
                label={t("common:labels.title")}
                value={cards[card.id]?.title ?? ""}
                {...loc}
                onChange={(v: LocalizedString) =>
                  setCard(card.id, { title: v || undefined })
                }
              />
              <LocalizedTextField
                label={t("common:labels.description")}
                value={cards[card.id]?.description ?? ""}
                multiline
                rows={2}
                {...loc}
                onChange={(v: LocalizedString) =>
                  setCard(card.id, { description: v || undefined })
                }
              />
            </Stack>
          </Box>
        ))}
      </Box>
    </Stack>
  );
}

// ── shared helpers ─────────────────────────────────────────────────────────

// ── Promotion slots (Announcement / QR) ────────────────────────────────────

/** Size choices the operator can pick for a promotion. */
const SLOT_SIZES: SlotSize[] = ["small", "medium", "large"];

/** Build a fresh, empty fill of the given type (operator supplies data after). */
function defaultSlotFill(type: SlotType): SlotFill {
  if (type === "qr") return { type: "qr", url: "", enabled: true };
  if (type === "offer") return { type: "offer", offerId: "", enabled: true };
  return { type: "announcement", body: "", enabled: true };
}

/** ISO instant → value for a <input type="datetime-local"> (local time, no tz). */
function isoToLocalInput(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate()
  )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** datetime-local value → ISO instant (or undefined when cleared/invalid). */
function localInputToIso(v: string): string | undefined {
  if (!v) return undefined;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/** The select value standing for `pick: "featured"`. Not a valid offer id. */
const FEATURED_PICK = "__featured__";

/**
 * What a featured fill will show, said before the operator saves (MEWS-7).
 *
 * Names the offers that qualify today, so "automatic" is never a mystery; and
 * when none do, says how to make one — including from the hotel's own PMS —
 * instead of leaving an empty slot to be discovered on a television.
 */
function FeaturedPickNote({
  offers,
  defaultLocale,
}: {
  offers: RoomOffer[];
  defaultLocale: string;
}) {
  const { t } = useTranslation(["templates", "common"]);
  const featured = offers.filter((o) => o.featured === true);
  const live = featured.filter((o) => o.enabled);
  const name = (o: RoomOffer) =>
    resolveLocalized(o.title, defaultLocale, defaultLocale) || o.id;
  if (featured.length === 0) {
    return (
      <Alert severity="warning" data-testid="featured-pick-none">
        {t("contentEditor.slots.featuredNone")}
      </Alert>
    );
  }
  return (
    <Alert severity="info" data-testid="featured-pick-note">
      {live.length > 0
        ? t("contentEditor.slots.featuredLeadNamed", {
            names: live.map(name).join(", "),
          })
        : t("contentEditor.slots.featuredLead")}{" "}
      {featured.length > live.length &&
        `${t("contentEditor.slots.featuredOff", {
          count: featured.length - live.length,
        })} `}
      {t("contentEditor.slots.featuredChanges")}
    </Alert>
  );
}

/** One line that says what a fill shows, for its row in the list. */
function slotSummary(
  t: Translate,
  fill: SlotFill,
  offers: RoomOffer[],
  defaultLocale: string
): string {
  const text = (v: LocalizedString | undefined) =>
    resolveLocalized(v, defaultLocale, defaultLocale).trim();
  if (fill.type === "announcement")
    return (
      text(fill.title) ||
      text(fill.body) ||
      t("templates:contentEditor.slots.summary.emptyAnnouncement")
    );
  if (fill.type === "qr")
    return (
      text(fill.label) ||
      fill.url ||
      t("templates:contentEditor.slots.summary.noLink")
    );
  if (fill.pick === "featured")
    return t("templates:contentEditor.slots.summary.featured");
  const offer = offers.find((o) => o.id === fill.offerId);
  if (!fill.offerId) return t("templates:contentEditor.slots.summary.noOffer");
  if (offer) return text(offer.title) || offer.id;
  // Only call it gone once the catalogue has loaded (same rule as the form).
  return offers.length > 0
    ? t("templates:contentEditor.slots.summary.offerGone")
    : t("templates:contentEditor.slots.summary.offer");
}

/** "From 1 Oct 12:00 · until 3 Oct 18:00", or "" when it always shows. */
function slotWindow(t: Translate, fmt: Formatters, fill: SlotFill): string {
  const when = (iso: string) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? ""
      : fmt.dateTime(d, {
          dateStyle: "medium",
          timeStyle: "short",
        });
  };
  const parts = [
    fill.startAt
      ? t("templates:contentEditor.slots.window.from", {
          date: when(fill.startAt),
        })
      : "",
    fill.endAt
      ? t("templates:contentEditor.slots.window.until", {
          date: when(fill.endAt),
        })
      : "",
  ].filter(Boolean);
  return parts.join(" · ");
}

/**
 * Promotions pane - one list per slot the template declares in its manifest.
 * The operator only fills/toggles content; the template owns where the slot
 * sits and how it's styled. Shown only for templates that expose slots.
 *
 * The lists stay on the page; adding a promotion opens a modal, editing one
 * opens a right-hand drawer. Both work on a local copy and write it into the
 * draft (`onChange`) only on Add / Apply - Cancel throws it away. The page's
 * own Save persists the draft, as before.
 */
export function SlotsEditor({
  content,
  manifest,
  supportedLocales,
  defaultLocale,
  onChange,
}: ContentPaneProps & { manifest: SchemeManifest }) {
  const { t } = useTranslation(["templates", "common"]);
  const defs = manifest.slots ?? [];
  const slots = content.slots ?? {};
  const premiseId = usePremiseScope();
  // The offer catalogue, for the picker on an `offer` fill (`POS-4`). Fetched
  // once here rather than per card, and only when this template actually has a
  // slot that takes one - the five care templates declare no slots at all and
  // must never call a hotel route. The premise route answers with the rows this
  // location inherits as well as its own, so a chain-level offer is pickable.
  const takesOffers = defs.some((d) => d.accepts.includes("offer"));
  const [offers, setOffers] = useState<RoomOffer[]>([]);
  useEffect(() => {
    if (!takesOffers || !premiseId) return;
    let alive = true;
    foyerApi
      .listRoomOffers({ kind: "premise", premiseId })
      .then((list) => alive && setOffers(list))
      .catch(() => alive && setOffers([]));
    return () => {
      alive = false;
    };
  }, [takesOffers, premiseId]);
  // Which fill the modal / drawer is on: `index: "new"` = adding to `defId`.
  const [editing, setEditing] = useState<{
    defId: string;
    index: number | "new";
  } | null>(null);
  const setFills = (id: string, fills: SlotFill[]) => {
    const next = { ...slots };
    // An empty list is an empty slot, not a slot holding nothing: leaving the
    // key behind would keep the slot "filled in" everywhere that counts keys.
    if (fills.length === 0) delete next[id];
    else next[id] = fills;
    onChange({ ...content, slots: next });
  };
  if (defs.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("contentEditor.slots.empty")}
      </Typography>
    );
  }
  const editingDef = editing && defs.find((d) => d.id === editing.defId);
  const editingFills = editingDef ? slotFills(slots, editingDef.id) : [];
  const editingFill =
    editing && editingDef
      ? editing.index === "new"
        ? defaultSlotFill(editingDef.accepts[0])
        : editingFills[editing.index]
      : undefined;
  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h6">{t("common:labels.promotions")}</Typography>
      </Box>
      {defs.map((def) => {
        const fills = slotFills(slots, def.id);
        const write = (next: SlotFill[]) =>
          setFills(def.id, next.filter(Boolean));
        return (
          <Stack key={def.id} spacing={2}>
            {fills.map((fill, i) => (
              <SlotCard
                key={`${def.id}-${i}`}
                def={def}
                index={i}
                total={fills.length}
                fill={fill}
                offers={offers}
                defaultLocale={defaultLocale}
                onEdit={() => setEditing({ defId: def.id, index: i })}
                onRemove={() => write(fills.filter((_, n) => n !== i))}
              />
            ))}
            {fills.length < MAX_SLOT_FILLS && (
              <Box>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={() => setEditing({ defId: def.id, index: "new" })}
                  data-testid={`slot-add-${def.id}`}
                >
                  {fills.length === 0
                    ? t("contentEditor.slots.addFirst")
                    : t("contentEditor.slots.addAnother")}
                </Button>
              </Box>
            )}
          </Stack>
        );
      })}
      {editing && editingDef && editingFill && (
        <SlotFillSurface
          key={`${editing.defId}-${editing.index}`}
          def={editingDef}
          mode={editing.index === "new" ? "create" : "edit"}
          title={
            editing.index === "new"
              ? t("contentEditor.slots.addTo", { slot: editingDef.label })
              : t("contentEditor.slots.editSlot", {
                  slot:
                    editingFills.length > 1
                      ? t("contentEditor.slots.numbered", {
                          slot: editingDef.label,
                          n: (editing.index as number) + 1,
                        })
                      : editingDef.label,
                })
          }
          initial={editingFill}
          offers={offers}
          supportedLocales={supportedLocales}
          defaultLocale={defaultLocale}
          onClose={() => setEditing(null)}
          onSave={(f) => {
            setFills(
              editingDef.id,
              editing.index === "new"
                ? [...editingFills, f]
                : editingFills.map((x, n) => (n === editing.index ? f : x))
            );
            setEditing(null);
          }}
        />
      )}
    </Stack>
  );
}

/** One promotion in a slot's list: what it shows, plus Edit and Remove. */
function SlotCard({
  def,
  index,
  total,
  fill,
  offers,
  defaultLocale,
  onEdit,
  onRemove,
}: {
  def: SchemeSlotDef;
  /** Position in the slot's list, for the header and the remove control. */
  index: number;
  total: number;
  fill: SlotFill;
  /** The offer catalogue, to name an `offer` fill. Empty until it loads. */
  offers: RoomOffer[];
  defaultLocale: string;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation(["templates", "common"]);
  const fmt = useFormat();
  const when = slotWindow(t, fmt, fill);
  const size = fill.size ?? "medium";
  const heading =
    total > 1
      ? t("contentEditor.slots.numbered", { slot: def.label, n: index + 1 })
      : def.label;
  return (
    <Box
      sx={{
        // Same card language as the Screens board: a surface that sits ON the
        // page with its own header strip, rather than a hairline rectangle that
        // reads as a fieldset. A promotion IS a thing you place, so it should
        // look like an object, not like a group of inputs.
        borderRadius: "14px",
        border: "1px solid",
        borderColor: tokens.divider,
        backgroundColor: tokens.surface,
        boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
        overflow: "hidden",
      }}
      data-testid={`slot-card-${def.id}`}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: "1px solid",
          borderColor: tokens.divider,
          backgroundColor: tokens.canvas,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography
            variant="meta"
            sx={{ flex: 1, typography: "eyebrow", color: tokens.inkMuted }}
          >
            {heading}
          </Typography>
          <Tooltip title={t("common:buttons.edit")}>
            <IconButton
              size="small"
              aria-label={t("contentEditor.slots.editSlot", { slot: heading })}
              onClick={onEdit}
              data-testid={`slot-edit-${def.id}-${index}`}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            color="inherit"
            onClick={onRemove}
            sx={{ minWidth: 0, color: tokens.inkFaint }}
            data-testid={`slot-remove-${def.id}-${index}`}
          >
            {t("common:buttons.remove")}
          </Button>
        </Stack>
      </Box>

      <Box
        role="button"
        tabIndex={0}
        onClick={onEdit}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onEdit();
          }
        }}
        sx={{
          p: 2,
          cursor: "pointer",
          "&:hover": { backgroundColor: tokens.rowAlt },
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: "fontWeightSemibold", color: tokens.ink }}
        >
          {slotSummary(t, fill, offers, defaultLocale)}
        </Typography>
        <Typography variant="meta" sx={{ mt: 0.25, color: tokens.inkMuted }}>
          {[
            t(`contentEditor.slots.typeLabel.${fill.type}`),
            t(`contentEditor.slots.size.${size}`),
            when,
          ]
            .filter(Boolean)
            .join(SEPARATOR)}
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * Add (modal) / edit (right drawer) one promotion. Works on a local copy;
 * Add / Apply hands it to `onSave` once the surface has closed, Cancel drops it.
 */
function SlotFillSurface({
  def,
  mode,
  title,
  initial,
  offers,
  supportedLocales,
  defaultLocale,
  onClose,
  onSave,
}: {
  def: SchemeSlotDef;
  mode: "create" | "edit";
  title: string;
  initial: SlotFill;
  offers: RoomOffer[];
  supportedLocales: string[];
  defaultLocale: string;
  onClose: () => void;
  onSave: (next: SlotFill) => void;
}) {
  const { t } = useTranslation(["templates", "common"]);
  const [draft, setDraft] = useState<SlotFill>(initial);
  return (
    <ItemFormSurface
      mode={mode}
      onClose={onClose}
      title={title}
      subtitle={def.help}
      data-testid={`slot-form-${def.id}`}
      actions={(dismiss) => (
        <>
          <Button onClick={() => dismiss()}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => dismiss(() => onSave(draft))}
            data-testid={`slot-save-${def.id}`}
          >
            {mode === "create"
              ? t("common:buttons.add")
              : t("common:buttons.apply")}
          </Button>
        </>
      )}
    >
      <SlotForm
        def={def}
        current={draft}
        offers={offers}
        supportedLocales={supportedLocales}
        defaultLocale={defaultLocale}
        onChange={setDraft}
      />
    </ItemFormSurface>
  );
}

/** The fields of one promotion. */
export function SlotForm({
  def,
  current,
  offers,
  supportedLocales,
  defaultLocale,
  onChange,
}: {
  def: SchemeSlotDef;
  current: SlotFill;
  /** The offer catalogue, for an `offer` fill's picker. Empty until it loads. */
  offers: RoomOffer[];
  supportedLocales: string[];
  defaultLocale: string;
  onChange: (next: SlotFill) => void;
}) {
  const { t } = useTranslation(["templates", "common"]);
  const loc = { supportedLocales, defaultLocale };
  // Visibility lives in the Toggles tab (`showPromotions`), not here - this tab
  // is only even shown when that toggle is on.

  const changeType = (type: SlotType) => {
    // Preserve the size and active window across a type switch; content shapes
    // differ so the type-specific fields reset.
    const base = {
      size: current.size,
      startAt: current.startAt,
      endAt: current.endAt,
    };
    onChange(
      type === "qr"
        ? { type: "qr", url: "", ...base }
        : type === "offer"
          ? { type: "offer", offerId: "", ...base }
          : { type: "announcement", body: "", ...base }
    );
  };

  return (
    <Stack spacing={2} sx={{ pt: 1 }}>
      {def.accepts.length > 1 && (
        <TextField
          select
          size="small"
          label={t("common:labels.type")}
          value={current.type}
          onChange={(e) => changeType(e.target.value as SlotType)}
          sx={{ maxWidth: 320 }}
        >
          {def.accepts.map((type) => (
            <MenuItem key={type} value={type}>
              {t(`contentEditor.slots.typeLabel.${type}`)}
            </MenuItem>
          ))}
        </TextField>
      )}

      <TextField
        select
        size="small"
        label={t("contentEditor.slotForm.size")}
        value={current.size ?? "medium"}
        onChange={(e) =>
          onChange({ ...current, size: e.target.value as SlotSize })
        }
        sx={{ maxWidth: 320 }}
      >
        {SLOT_SIZES.map((s) => (
          <MenuItem key={s} value={s}>
            {t(`contentEditor.slots.size.${s}`)}
          </MenuItem>
        ))}
      </TextField>

      {current.type === "announcement" && (
        <>
          <LocalizedTextField
            label={t("contentEditor.slotForm.announcementTitle")}
            value={current.title}
            {...loc}
            placeholder={t(
              "contentEditor.slotForm.announcementTitlePlaceholder"
            )}
            onChange={(title) => onChange({ ...current, title })}
          />
          <LocalizedTextField
            label={t("contentEditor.slotForm.body")}
            value={current.body}
            {...loc}
            multiline
            rows={2}
            placeholder={t("contentEditor.slotForm.bodyPlaceholder")}
            onChange={(body) => onChange({ ...current, body })}
          />
          <AssetField
            label={t("contentEditor.slotForm.image")}
            value={current.imageUrl ?? ""}
            preview
            onChange={(imageUrl) =>
              onChange({ ...current, imageUrl: imageUrl || undefined })
            }
          />
        </>
      )}

      {current.type === "qr" && (
        <>
          <TextField
            size="small"
            fullWidth
            label={t("contentEditor.slotForm.link")}
            value={current.url}
            placeholder={QR_URL_PLACEHOLDER}
            onChange={(e) => onChange({ ...current, url: e.target.value })}
          />
          <LocalizedTextField
            label={t("contentEditor.slotForm.label")}
            value={current.label}
            {...loc}
            placeholder={t("contentEditor.slotForm.labelPlaceholder")}
            onChange={(label) => onChange({ ...current, label })}
          />
        </>
      )}

      {current.type === "offer" && (
        <>
          <TextField
            select
            size="small"
            label={t("contentEditor.slots.summary.offer")}
            value={
              current.pick === "featured"
                ? FEATURED_PICK
                : offers.some((o) => o.id === current.offerId)
                  ? current.offerId
                  : ""
            }
            onChange={(e) => {
              const v = e.target.value;
              // `pick` and `offerId` are alternatives: choosing one clears the
              // other, so a saved fill never says both.
              const { pick: _pick, ...rest } = current;
              onChange(
                v === FEATURED_PICK
                  ? { ...rest, offerId: "", pick: "featured" }
                  : { ...rest, offerId: v }
              );
            }}
            sx={{ maxWidth: 420 }}
            inputProps={{ "data-testid": "slot-offer-select" }}
          >
            <MenuItem value={FEATURED_PICK}>
              {t("contentEditor.slots.summary.featured")}
            </MenuItem>
            <MenuItem value="">
              <em>{t("contentEditor.slotForm.notChosen")}</em>
            </MenuItem>
            {offers.map((o) => {
              const name =
                resolveLocalized(o.title, defaultLocale, defaultLocale) || o.id;
              return (
                <MenuItem key={o.id} value={o.id}>
                  {o.enabled
                    ? name
                    : t("contentEditor.slotForm.offerSwitchedOff", { name })}
                </MenuItem>
              );
            })}
          </TextField>
          {current.pick === "featured" && (
            <FeaturedPickNote offers={offers} defaultLocale={defaultLocale} />
          )}
          {!current.offerId && current.pick !== "featured" && (
            <Alert severity="info">
              {t("contentEditor.slotForm.pickOffer")}
            </Alert>
          )}
          {!!current.offerId &&
            offers.length > 0 &&
            !offers.some((o) => o.id === current.offerId) && (
              <Alert severity="warning">
                {t("contentEditor.slotForm.offerGone")}
              </Alert>
            )}
        </>
      )}

      <Box sx={fieldGrid(220)}>
        <TextField
          size="small"
          type="datetime-local"
          label={t("contentEditor.slotForm.showFrom")}
          InputLabelProps={{ shrink: true }}
          value={isoToLocalInput(current.startAt)}
          onChange={(e) =>
            onChange({ ...current, startAt: localInputToIso(e.target.value) })
          }
        />
        <TextField
          size="small"
          type="datetime-local"
          label={t("contentEditor.slotForm.showUntil")}
          InputLabelProps={{ shrink: true }}
          value={isoToLocalInput(current.endAt)}
          onChange={(e) =>
            onChange({ ...current, endAt: localInputToIso(e.target.value) })
          }
        />
      </Box>
    </Stack>
  );
}
